/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import currency from "currency.js";
import Joi from "joi";
import { SettingsStyleEdit } from "cms/fields/layout/controls/settings-style";
import { useSelectFieldSchema } from "cms/fields/hooks/use-select-field-schema";
import { DisplaySettingsWrapper } from "cms/fields/layout/display-settings-wrapper";
import { ListItemContext } from "cms/fields/field-lists/field-list-context";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { isFalse, isTrue } from "utils/empty-exists";

const CurrencyFieldItemEdit = () => {
    
    const { t } = useTranslation();
    const {
        name,
        type,
        index,
        value,
        settings,
        updateListItem,
        disabled = false
    } = useContext(ListItemContext);
    
    const schema = useSelectFieldSchema(name);
    const { settings: schemaSettings } = schema || {};
    const { styleOptions } = schemaSettings || {};
    
    const [currencyValue, setCurrencyValue] = useState(value || 0);
    const [currencyValueFormatted, setCurrencyValueFormatted] = useState();
    const [error, setError] = useState();
    const [isValid, setIsValid] = useState();
    const [itemLocalState, setItemLocalState] = useState({ value: currencyValue, settings });
    
    useEffect(() => {
        validate(currencyValue);
        format(currencyValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currencyValue]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const update = useCallback(
        debounce((updateState, index) => {
            updateListItem(updateState, index);
        }, 1200),
        [updateListItem]
    );
    const onSettingsChange = (updateState) => {
        setItemLocalState(updateState);
        update(updateState, index);
    };
    const onChange = (event) => {
        const newValue = event.target.value;
        setCurrencyValue(newValue);
        const valid = validate(newValue);
        format(newValue);
        const newState = { ...itemLocalState, value: newValue };
        setItemLocalState(newState);
        if (valid) {
            update(newState, index);
        }
    };
    const validate = (input) => {
        let valid = false;
        const schema =
            Joi.number().positive().allow(0).precision(2).required();
        const result = schema.validate(input);
        if (result.error) {
            setError(result.error.message);
            valid = false;
            setIsValid(valid);
        } else {
            setError(null);
            valid = true;
            setIsValid(valid);
        }
        return valid;
    };
    const format = (input) => {
        if (isFalse(validate(input))) {
            setCurrencyValueFormatted("Not a valid currency value");
            return;
        }
        const currencyOptions = {
            separator: ",",
            decimal: ".",
            precision: 2,
            // format: formatFunc
        };
        const currencyValueFormatted = currency(
            input,
            currencyOptions
        ).format();
        setCurrencyValueFormatted(currencyValueFormatted);
    };

    return (
        <Fragment>
            <div className='simplicity-composer-field-primary-layout'>
                <div className='simplicity-composer-field-primary'>
                    <div className='simplicity-field-currency'>
                        <div className='simplicity-field-currency-editor'>
                            <div>
                                <input
                                    className='simplicity-field-currency-input'
                                    type='text'
                                    inputMode='numeric'
                                    pattern='[0-9]*'
                                    value={currencyValue}
                                    onChange={onChange}
                                />
                            </div>
                            <div>
                                <div className='icon'>
                                    {isTrue(isValid) &&
                                        <CheckBoxIcon
                                            className='check-icon'
                                            // style={{color: 'green'}}
                                        />
                                    }
                                    {isFalse(isValid) &&
                                        <DisabledByDefaultIcon
                                            className='ex-icon'
                                            // style={{color: 'red'}}
                                        />
                                    }
                                </div>
                            </div>
                        </div>
                        <p className='formmatted number'>{currencyValueFormatted}</p>
                        <p>{error}</p>
                        
                    </div>
                </div>
                <DisplaySettingsWrapper>
                    {/*  Reads from schema options.styleOptions */}
                    {/*  records selection as value settings.style */}
                    <SettingsStyleEdit
                        type={type}
                        name={name}
                        label={t("commons.displayStyle")}
                        itemLocalState={itemLocalState}
                        setItemLocalState={setItemLocalState}
                        disabled={disabled}
                        styleOptions={styleOptions}
                        selected={itemLocalState?.settings?.style}
                        index={index}
                        update={onSettingsChange}
                    />
                </DisplaySettingsWrapper>
            </div>
        </Fragment>
    );
};
CurrencyFieldItemEdit.displayName = "CurrencyFieldItemEdit";
export { CurrencyFieldItemEdit };
